/**
 * usePreloadPhaseTransition — Preloads the assets needed for a phase transition:
 * - The next room's background image (displayed in the transition overlay reveal)
 * - The next room's background video (displayed once gameplay resumes)
 *
 * Triggered when nextRoom is set in the game store (which happens automatically
 * when the current message is a PreRoomTransition message).
 * Returns a status that the PhaseTransitionOverlay can gate on.
 *
 * Image preloading is fire-and-forget via <link rel="preload"> — there is no
 * completion callback. Status is set to "ready" immediately after enqueuing,
 * trusting that the browser will have the image cached by the time it's rendered.
 *
 * This hook does NOT preload message-level assets (item images, meme icons, etc.)
 * — those are handled by usePreloadMessageAssets once the new phase starts.
 */

import { useEffect, useRef, useState } from "react";
import { useGameStore } from "@/state/gameStore";
import {preloadManager} from "@/libs/assetPreloading/PreloadManager";

export type PhaseTransitionPreloadStatus = "idle" | "loading" | "ready";

export function usePreloadPhaseTransition(): PhaseTransitionPreloadStatus {
    const [status, setStatus] = useState<PhaseTransitionPreloadStatus>("idle");
    const nextRoom = useGameStore((s) => s.nextRoom);
    const preloadedForRef = useRef<string | null>(null);

    useEffect(() => {
        if (!nextRoom) return;

        const roomKey = nextRoom.documentId ?? nextRoom.image?.url ?? null;
        if (!roomKey || preloadedForRef.current === roomKey) return;
        preloadedForRef.current = roomKey;

        try {
            if (nextRoom.image) {
                preloadManager.enqueueImages([nextRoom.image], "lg", "critical");
            }

            if (nextRoom.animated) {
                preloadManager.enqueueVideo(nextRoom.animated, "critical");
            }
        } catch (err) {
            console.warn("Failed to preload phase transition assets:", err);
        }

        // Set ready immediately — image preload is fire-and-forget,
        // the browser will cache it before the transition overlay renders it.
        if (preloadedForRef.current === roomKey) {
            setStatus("ready");
        }
    }, [nextRoom]);

    return status;
}